import { useState, useRef } from 'react'
import clsx from 'clsx'
import { useLazyQuery, useMutation, gql } from '@apollo/client'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

import { ConfirmDialog } from '../components'
import { generateReport, makeCSV } from '../utils'
import { RESET_OFFERING_MUTATION } from '../services/admin'
import { apolloOfferingStatusData } from '../services/apollo'

const GET_USERS = gql`
  query GetUser {
    users(first: 1000) {
      edges {
        node {
          firstName
          lastName
          email
          points
          capabilities
          completion
          transactions {
            bonus
            timestamp
            updaterId
            userId
          }
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS = gql`
  query GetAgendaItems {
    agendaItems(first: 50) {
      edges {
        node {
          order
          postId
          title
          detail {
            knowledgeCheck {
              questions {
                point
              }
            }
          }
        }
      }
    }
  }
`
const GET_LEARNING_LOUNGE_ITEMS = gql`
  query GetLearningLoungeItems {
    learningLoungeItems(first: 200) {
      edges {
        node {
          order
          postId
          title
          detail {
            questions {
              point
            }
          }
        }
      }
    }
  }
`
const GET_OFFERINGS = gql`
  query GetOfferings {
    offerings(first: 1) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`

const AdminEndOfferingWizard = ({ cancelOffering = () => {} }) => {
  const [step, setStep] = useState(1)

  const handlePrev = (e) => {
    e.preventDefault()

    setStep((currentStep) => --currentStep)
  }

  const handleNext = (e) => {
    e.preventDefault()

    setStep((currentStep) => ++currentStep)
  }

  return (
    <div className="flex flex-1 basis-auto flex-col">
      <div className="mb-6 flex">
        <div className="relative flex flex-1 flex-col items-center">
          <StepIcon
            active={step >= 1}
            label={1}
            className="z-[1] h-[30px] w-[30px] self-start"
          />
        </div>
        <div className="relative flex flex-1 flex-col items-center">
          <div className="absolute left-[calc(-100%+15px)] right-[15px] top-[10px] flex-auto">
            <span
              className={clsx(
                'block h-[10px] transition-colors',
                step < 2 ? 'bg-bright-gray' : 'bg-flame'
              )}
            ></span>
          </div>
          <StepIcon
            active={step === 2}
            label={2}
            className="z-[1] h-[30px] w-[30px] self-end"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        {/** Step 1: Download the report */}
        {step === 1 && <Step1 cancelOffering={cancelOffering} />}

        {/** Step 2: Confirm platform reset */}
        {step === 2 && <Step2 cancelOffering={cancelOffering} />}
      </div>

      {/** Nav buttons */}
      <div className="flex justify-end">
        <button
          disabled={step === 1}
          className={clsx(
            'flex h-[35px] w-[35px] items-center justify-center rounded-full',
            step === 1
              ? 'cursor-not-allowed bg-bright-gray'
              : 'bg-flame active:translate-y-[0.75px] active:scale-[0.99]'
          )}
          onClick={handlePrev}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16.32 25.29"
            className="h-3/5 w-3/5 -translate-x-[2.5px]"
          >
            <g id="chevron-left-icon">
              <path
                d="M14.47,25.29a1.48,1.48,0,0,1-1-.36L.53,14A1.54,1.54,0,0,1,0,12.82a1.47,1.47,0,0,1,.53-1.14L13.85.36a1.5,1.5,0,1,1,1.94,2.28l-12,10.18,11.62,9.82a1.5,1.5,0,0,1,.17,2.12A1.48,1.48,0,0,1,14.47,25.29Z"
                style={{ fill: '#fff' }}
              />
            </g>
          </svg>
        </button>
        <button
          disabled={step === 2}
          className={clsx(
            'ml-4 flex h-[35px] w-[35px] items-center justify-center rounded-full',
            step === 2
              ? 'cursor-not-allowed bg-bright-gray'
              : 'bg-flame active:translate-y-[0.75px] active:scale-[0.99]'
          )}
          onClick={handleNext}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16.32 25.29"
            className="h-3/5 w-3/5 translate-x-[2.5px]"
          >
            <g id="chevron-right-icon">
              <path
                d="M1.5,25.29a1.48,1.48,0,0,1-1.14-.53,1.5,1.5,0,0,1,.17-2.12l12-10.17L.89,2.65A1.5,1.5,0,0,1,2.82.35l13,11a1.49,1.49,0,0,1,.53,1.14,1.51,1.51,0,0,1-.53,1.15L2.47,24.93A1.5,1.5,0,0,1,1.5,25.29Z"
                style={{ fill: '#fff' }}
              />
            </g>
          </svg>
        </button>
      </div>
    </div>
  )
}

const StepIcon = ({ label, className, active = false }) => (
  <svg
    className={className}
    focusable="false"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className={active ? 'fill-flame' : 'fill-bright-gray'}
      cx="12"
      cy="12"
      r="12"
    ></circle>
    <text
      className={active ? 'fill-white' : 'fill-dark-gray'}
      x="12"
      y="16"
      textAnchor="middle"
    >
      {label}
    </text>
  </svg>
)

/**
 * Step 1 component: Download a TMC Report
 *
 * @param {*} props - Component props
 * @returns Component
 */
const Step1 = ({ cancelOffering }) => {
  const fullReportRef = useRef(null)
  const [getUsers] = useLazyQuery(GET_USERS, {
    fetchPolicy: 'no-cache',
  })
  const [getAgendaItems] = useLazyQuery(GET_AGENDA_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [getLearningLoungeItems] = useLazyQuery(GET_LEARNING_LOUNGE_ITEMS, {
    fetchPolicy: 'no-cache',
  })

  const handleDownloadFullReport = async () => {
    const [usersQuery, agendaItemsQuery, learningLoungeItemsQuery] =
      await Promise.all([
        getUsers(),
        getAgendaItems(),
        getLearningLoungeItems(),
      ])

    const users = usersQuery.data.users.edges.map((user) => user.node)
    const agendaItems = agendaItemsQuery.data.agendaItems.edges
      .map((agendaItem) => agendaItem.node)
      .sort((a, b) => a.order - b.order)
    const learningLoungeItems =
      learningLoungeItemsQuery.data.learningLoungeItems.edges
        .map((learningLoungeItem) => learningLoungeItem.node)
        .sort((a, b) => a.order - b.order)
    const contents = generateReport(users, agendaItems, learningLoungeItems)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    fullReportRef.current.href = downloadUrl
    fullReportRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Full report downloaded.')
  }

  return (
    <>
      <p className="font-bold">Step 1&#41; Download a TMC Report</p>
      <p>
        Click the button below to download an auto-generated report for this
        offering of TMC.
      </p>
      <p>The report includes the following participant data:</p>
      <ul>
        <li>Names</li>
        <li>Email addresses</li>
        <li>Completion status of Quick Hits</li>
        <li>Point totals</li>
      </ul>

      {/** Buttons */}
      <div className="mt-[75px] text-center">
        <div className="inline-flex flex-col items-center">
          <div>
            <button
              type="button"
              className="mx-2 mt-4 rounded-full bg-flame px-6 py-3 font-bold leading-none text-white transition-colors hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]"
              onClick={handleDownloadFullReport}
            >
              Download Report
            </button>
            <a
              className="hidden"
              download={`TMC-Report_${format(
                new Date(),
                'yyyy-LL-dd_HHmmss'
              )}.csv`}
              href=" "
              ref={fullReportRef}
            >
              download full report
            </a>
          </div>

          <button
            className="mx-2 mt-4 rounded-full bg-bright-gray px-6 py-2 font-bold leading-none text-[#a0a0a0] opacity-80 transition-colors hover:opacity-100 active:translate-y-[0.75px] active:scale-[0.99]"
            onClick={cancelOffering}
          >
            Cancel and return to TMC
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Step 2 component: Confirm Platform Reset
 *
 * @param {*} props - Component props
 * @returns Component
 */
const Step2 = ({ cancelOffering }) => {
  let navigate = useNavigate()
  const [resetOffering, { loading: resetting }] = useMutation(
    RESET_OFFERING_MUTATION
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [getOfferings] = useLazyQuery(GET_OFFERINGS, {
    fetchPolicy: 'no-cache',
  })

  const handleConfirmReset = () => {
    setConfirmOpen(true)
  }

  const handleReset = async () => {
    // Close the confirm dialog
    setConfirmOpen(false)

    const offeringsQuery = await getOfferings()
    const offerings = offeringsQuery.data.offerings.edges.map(
      (offering) => offering.node
    )

    if (offerings.length === 0) {
      toast.error('Something went wrong. No active offering has been found.')
      return
    }

    try {
      await resetOffering({
        variables: {
          id: offerings[0].databaseId,
        },
      })

      // Show toast notification
      toast.success(
        'You have successfully ended this TMC offering. The only active account at this time is the administrator account.',
        { autoClose: false }
      )

      // Navigate back to the new offering tab
      navigate('/admin/new', { replace: true })

      // Signal offering reset status
      apolloOfferingStatusData({ created: false })
    } catch (err) {
      console.error(`Reset offering failed :: err`, JSON.stringify(err))

      // Show toast error notification
      toast.error('Reset offering failed. Please try again!')
    }
  }

  return (
    <>
      {/** Confirm dialog for "change user's role" action */}
      <ConfirmDialog
        open={confirmOpen}
        title="Are you sure?"
        text="You won't be able to revert this."
        setOpen={setConfirmOpen}
        onConfirm={handleReset}
        onCancel={() => setConfirmOpen(false)}
      />
      <p className="font-bold">Step 2&#41; Confirm Platform Reset</p>
      <p>Click the button below to confirm the reset.</p>
      <p>
        This will officially end this offering of TMC and reset the platform to
        its initial state.
      </p>
      <p>
        To set up the next offering of TMC, log in as administrator and use the
        tab on the left, "New TMC Offering"
      </p>

      {/** Buttons */}
      <div className="mt-[75px] text-center">
        <div className="inline-flex flex-col items-center">
          <button
            type="button"
            disabled={resetting}
            className={clsx(
              'mx-2 mt-4 rounded-full bg-flame px-6 py-3 font-bold leading-none text-white transition-colors',
              resetting
                ? 'cursor-not-allowed'
                : 'hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]'
            )}
            onClick={handleConfirmReset}
          >
            {resetting ? 'PLEASE WAIT...' : 'CONFIRM PLATFORM RESET'}
          </button>
          <button
            type="button"
            disabled={resetting}
            className={clsx(
              'mx-2 mt-4 rounded-full bg-bright-gray px-6 py-2 font-bold leading-none text-[#a0a0a0] opacity-80 transition-colors ',
              resetting
                ? 'cursor-not-allowed'
                : 'hover:opacity-100 active:translate-y-[0.75px] active:scale-[0.99]'
            )}
            onClick={cancelOffering}
          >
            Cancel and return to TMC
          </button>
        </div>
      </div>
    </>
  )
}

export default AdminEndOfferingWizard
